import * as React from 'react';
import {
    Box,
    IconButton,
    Typography,
    Tooltip,
    useTheme,
    alpha
} from '@mui/material';
import { HideSource, Visibility } from '@mui/icons-material';

const semanaLabels = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

export default function CalendarioSemanaPanel(props) {
    const { data, eventos, setShowEvento, setIdEvento, feriado } = props;
    const theme = useTheme();
    const [hideEmpty, setHideEmpty] = React.useState(false);

    const diasDaSemana = React.useMemo(() => {
        const start = new Date(data);
        start.setDate(start.getDate() - start.getDay());
        start.setHours(0, 0, 0, 0);

        return Array.from({ length: 7 }).map((_, i) => {
            const d = new Date(start);
            d.setDate(d.getDate() + i);
            return d;
        });
    }, [data]);

    const feriadosMap = React.useMemo(() => {
        if (!feriado || !feriado.lst) return {};
        const map = {};
        feriado.lst.forEach(e => {
            const [ano, mes, dia] = e.date.split('-');
            map[`${dia}-${mes}-${ano}`] = e;
        });
        return map;
    }, [feriado]);

    const gradeEventos = React.useMemo(() => {
        if (!eventos) return [];

        const horas = [];
        for (let h = 0; h < 24; h++) {
            const dias = diasDaSemana.map(dia => {
                const dtInicioHora = new Date(dia);
                dtInicioHora.setHours(h, 0, 0, 0);

                const dtFimHora = new Date(dia);
                dtFimHora.setHours(h, 59, 59, 999);

                return eventos.filter(e => {
                    const evtStart = new Date(e.dtInicial);
                    let evtEnd = e.dtFinal ? new Date(e.dtFinal) : new Date(e.dtInicial);
                    evtEnd.setHours(23, 59, 59, 0);
                    return evtStart <= dtFimHora && evtEnd >= dtInicioHora && evtStart.getHours() === h;
                });
            });

            horas.push({
                horaNum: h,
                label: `${h.toString().padStart(2, '0')}:00`,
                dias: dias
            });
        }

        if (hideEmpty) {
            return horas.filter(h => h.dias.some(eventosNoDia => eventosNoDia.length > 0));
        }

        return horas;
    }, [diasDaSemana, eventos, hideEmpty]);

    const getBgColorHeader = (dt) => {
        const key = `${String(dt.getDate()).padStart(2, '0')}-${String(dt.getMonth() + 1).padStart(2, '0')}-${dt.getFullYear()}`;

        if (feriadosMap[key]) {
            return alpha(theme.palette.error.light, 0.15);
        }

        const hoje = new Date();
        if (dt.getDate() === hoje.getDate() && dt.getMonth() === hoje.getMonth() && dt.getFullYear() === hoje.getFullYear()) {
            return theme.palette.action.selected;
        }

        return theme.palette.grey[50];
    };

    const gridTemplate = '60px repeat(7, 1fr)';

    return (
        <Box
            sx={{
                width: '100%',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                height: '600px',
                bgcolor: 'background.paper',
                overflowY: 'auto',
                position: 'relative'
            }}
        >
            <Box
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    display: 'grid',
                    gridTemplateColumns: gridTemplate,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    bgcolor: theme.palette.grey[100],
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', borderRight: `1px solid ${theme.palette.divider}` }}>
                    <Tooltip title={hideEmpty ? "Mostrar todas as linhas" : "Ocultar linhas vazias"}>
                        <IconButton size="small" color="primary" onClick={() => setHideEmpty(!hideEmpty)}>
                            {hideEmpty ? <Visibility fontSize="small" /> : <HideSource fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                </Box>

                {diasDaSemana.map((dia, idx) => (
                    <Box
                        key={idx}
                        sx={{
                            p: 1,
                            textAlign: 'center',
                            borderRight: idx < 6 ? `1px solid ${theme.palette.divider}` : 'none',
                            bgcolor: getBgColorHeader(dia)
                        }}
                    >
                        <Typography variant="subtitle2" color="text.secondary">
                            {semanaLabels[dia.getDay()]}
                        </Typography>
                        <Typography variant="body1" fontWeight="500">
                            {dia.getDate()}
                        </Typography>
                    </Box>
                ))}
            </Box>

            <Box >
                {gradeEventos.map((linha) => (
                    <Box
                        key={`hora-${linha.horaNum}`}
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: gridTemplate,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            height: '60px',
                            '&:last-child': { borderBottom: 'none' }
                        }}
                    >
                        <Box
                            sx={{
                                p: 1,
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'center',
                                borderRight: `1px solid ${theme.palette.divider}`,
                                bgcolor: theme.palette.grey[50]
                            }}
                        >
                            <Typography variant="caption" color="text.secondary" fontWeight="500">
                                {linha.label}
                            </Typography>
                        </Box>

                        {linha.dias.map((eventosDoDia, idx) => (
                            <Box
                                key={`cel-${linha.horaNum}-${idx}`}
                                sx={{
                                    p: 0.5,
                                    borderRight: idx < 6 ? `1px solid ${theme.palette.divider}` : 'none',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    gap: 0.5,
                                    overflow: 'hidden',
                                    '&:hover': { bgcolor: alpha(theme.palette.primary.light, 0.05) }
                                }}
                            >
                                {eventosDoDia.map((evt) => {
                                    const dt = new Date(evt.dtInicial);
                                    const min = dt.getMinutes();
                                    const minStr = min < 10 ? `0${min}` : min;
                                    const mostrarMinuto = min > 0;

                                    return (
                                        <Box
                                            key={evt.id}
                                            onClick={() => {
                                                setIdEvento(evt.id);
                                                setShowEvento(true);
                                            }}
                                            sx={{
                                                flex:1 ,
                                                minWidth: 0,
                                                bgcolor: theme.palette.primary.light,
                                                color: theme.palette.primary.contrastText,
                                                px: 1,
                                                py: 0.5,
                                                borderRadius: 1,
                                                fontSize: '0.75rem',
                                                cursor: 'pointer',
                                                boxShadow: 1,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                transition: 'opacity 0.2s',
                                                '&:hover': { opacity: 0.8 }
                                            }}
                                        >
                                            {mostrarMinuto && (
                                                <Box component="span" sx={{ fontWeight: 'bold', mr: 0.5 }}>
                                                    {minStr}'
                                                </Box>
                                            )}
                                            {evt.nomeCurto}
                                        </Box>
                                    );
                                })}
                            </Box>
                        ))}
                    </Box>
                ))}
            </Box>
        </Box>
    );
}